import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import { Alert, Card, Descriptions, Spin, Tag, Typography } from 'antd'

import { FormattedDate } from '@/components/common/FormattedDate'
import { MONO_STYLE } from '@/components/common/MonoId'
import { VODOPAD_ENDPOINT } from '@/constants/constants'

type AdminVirtualMachineDto = components['schemas']['AdminVirtualMachineDto']

function usd(value: string | null | undefined) {
  if (value === null || value === undefined) return '—'
  const parsed = Number(value)
  return Number.isNaN(parsed) ? value : `$${parsed.toFixed(2)}`
}

// GET /v1/admin/vms/{id} carries the billing and chain view of a VM that the
// v2 list DTO omits.
export function VmBillingCard({ vmId }: { vmId: string }) {
  const query = useCustom<AdminVirtualMachineDto>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/vms/${vmId}`,
    method: 'get',
    queryOptions: { enabled: Boolean(vmId) },
  })

  const vm = query.query.data?.data as AdminVirtualMachineDto | undefined

  if (query.query.isLoading) {
    return (
      <Card title="Billing & Placement">
        <Spin />
      </Card>
    )
  }

  if (!vm) {
    return (
      <Card title="Billing & Placement">
        <Alert type="error" message="Failed to load billing" showIcon />
      </Card>
    )
  }

  return (
    <Card title="Billing & Placement">
      <Descriptions column={2} size="small">
        <Descriptions.Item label="Total Spent">
          {usd(vm.totalSpent)}
        </Descriptions.Item>
        <Descriptions.Item label="Price / Epoch">
          {usd(vm.pricePerEpoch)}
        </Descriptions.Item>
        <Descriptions.Item label="Reserved Balance">
          {usd(vm.reservedBalance)}
        </Descriptions.Item>
        <Descriptions.Item label="Next Billing">
          <FormattedDate date={vm.nextBillingAt} fmt="PPp" />
        </Descriptions.Item>
        <Descriptions.Item label="Status Changed">
          <FormattedDate date={vm.statusChangedAt} fmt="PPp" />
        </Descriptions.Item>
        <Descriptions.Item label="OS Image">
          {vm.osImage ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Provider">
          {vm.providerName ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Datacenter">
          {vm.datacenter
            ? `${vm.datacenter.country_code}, ${vm.datacenter.city_code} (tier ${vm.datacenter.tier})`
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Deal">
          {vm.dealId ? (
            <Typography.Text copyable style={MONO_STYLE}>
              {vm.dealId}
            </Typography.Text>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Peer">
          {vm.peerId ? (
            <Typography.Text copyable style={MONO_STYLE}>
              {vm.peerId}
            </Typography.Text>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Open Ports" span={2}>
          {vm.ports?.length
            ? vm.ports.map((port) => (
                <Tag key={`${port.protocol}-${port.port}`}>
                  {port.port}/{port.protocol}
                </Tag>
              ))
            : '—'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}
